<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, type ComponentPublicInstance, watch } from 'vue'

export type CommonTabItem = {
  label: string
  value: string | number
}

const props = withDefaults(
  defineProps<{
    tabs: CommonTabItem[]
    modelValue: CommonTabItem['value']
    ariaLabel?: string
    disabled?: boolean
    maskWidth?: number
  }>(),
  {
    ariaLabel: 'Tabs',
    disabled: false,
    maskWidth: 32,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: CommonTabItem['value']]
  change: [tab: CommonTabItem, index: number]
}>()

const tabsRef = ref<HTMLElement | null>(null)
const tabButtonRefs = ref<HTMLElement[]>([])
const showRightMask = ref(false)

/** 保存每个 Tab 按钮的 DOM 引用，以支持选中项自动滚动和键盘焦点循环。 */
const setTabButtonRef = (el: Element | ComponentPublicInstance | null, index: number) => {
  if (!(el instanceof HTMLElement)) {
    return
  }

  tabButtonRefs.value[index] = el
}

/** 根据横向滚动位置决定是否展示右侧渐隐遮罩。 */
const updateTabMask = () => {
  const tabsEl = tabsRef.value

  if (!tabsEl) {
    showRightMask.value = false
    return
  }

  const maxScrollLeft = tabsEl.scrollWidth - tabsEl.clientWidth

  showRightMask.value = maxScrollLeft > 1 && tabsEl.scrollLeft < maxScrollLeft - 1
}

/** 将选中项和其后一项一并纳入可视区域，避免选择后被右侧遮罩遮挡。 */
const scrollTabIntoView = (index: number) => {
  const tabsEl = tabsRef.value
  const targetTab = tabButtonRefs.value[index]
  const nextTab = tabButtonRefs.value[index + 1]

  if (!tabsEl || !targetTab) {
    return
  }

  const maskWidth = props.maskWidth
  const safeGap = 12
  const currentLeft = tabsEl.scrollLeft
  const tabLeft = targetTab.offsetLeft
  const tabRight = tabLeft + targetTab.offsetWidth
  const triggerRight = nextTab ? nextTab.offsetLeft + nextTab.offsetWidth : tabRight
  const visibleLeft = currentLeft + safeGap
  const visibleRight = currentLeft + tabsEl.clientWidth - maskWidth - safeGap

  let nextLeft = currentLeft

  if (triggerRight > visibleRight) {
    nextLeft = triggerRight - (tabsEl.clientWidth - maskWidth - safeGap)
  } else if (tabLeft < visibleLeft) {
    nextLeft = tabLeft - safeGap
  }

  const clampedLeft = Math.max(0, Math.min(nextLeft, tabsEl.scrollWidth - tabsEl.clientWidth))

  if (Math.abs(clampedLeft - currentLeft) > 0.5) {
    tabsEl.scrollTo({ left: clampedLeft, behavior: 'smooth' })
  }
}

/** 选择 Tab 并向父组件同步值及完整的 Tab 数据。 */
const selectTab = async (tab: CommonTabItem, index: number) => {
  if (props.disabled && tab.value === props.modelValue) {
    return
  }

  emit('update:modelValue', tab.value)
  emit('change', tab, index)

  await nextTick()
  scrollTabIntoView(index)
  requestAnimationFrame(updateTabMask)
}

/** 键盘焦点到达最后一个分类后，按 Tab 从首个分类继续循环。 */
const onTabKeydown = (event: KeyboardEvent, index: number) => {
  if (event.key !== 'Tab' || event.shiftKey || index !== props.tabs.length - 1 || props.tabs.length < 2) {
    return
  }

  event.preventDefault()
  tabButtonRefs.value[0]?.focus()
}

watch(
  () => props.tabs,
  () => {
    void nextTick(updateTabMask)
  },
  { deep: true },
)

onMounted(() => {
  void nextTick(updateTabMask)
  tabsRef.value?.addEventListener('scroll', updateTabMask, { passive: true })
  window.addEventListener('resize', updateTabMask)
})

onBeforeUnmount(() => {
  tabsRef.value?.removeEventListener('scroll', updateTabMask)
  window.removeEventListener('resize', updateTabMask)
})
</script>

<template>
  <div class="common-tabs-wrap" :style="{ '--common-tabs-mask-width': `${maskWidth}px` }">
    <div ref="tabsRef" class="common-tabs" role="tablist" :aria-label="ariaLabel">
      <button
        v-for="(tab, index) in tabs"
        :key="tab.value"
        :ref="(el) => setTabButtonRef(el, index)"
        type="button"
        class="common-tab cursor-pointer hover:bg-[#F8F8F8]"
        :class="{ 'is-active': tab.value === modelValue }"
        role="tab"
        :aria-selected="tab.value === modelValue"
        :aria-disabled="disabled && tab.value === modelValue"
        @click="selectTab(tab, index)"
        @keydown="onTabKeydown($event, index)"
      >
        <slot name="label" :tab="tab" :index="index">{{ tab.label }}</slot>
      </button>
    </div>
    <div v-show="showRightMask" class="common-tabs-mask" aria-hidden="true"></div>
  </div>
</template>

<style scoped lang="less">
.common-tabs-wrap {
  position: relative;
  width: var(--common-tabs-width, 1100px);
  max-width: var(--common-tabs-max-width, calc(100% - 100px));
  min-width: 0;
  flex: 0 1 auto;
}

.common-tabs {
  display: flex;
  align-items: center;
  gap: 0;
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  padding: var(--common-tabs-padding, 12px 0);
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.common-tab {
  flex-shrink: 0;
  box-sizing: border-box;
  border: 0.5px solid rgba(11, 18, 40, 0.14);
  border-radius: 2px;
  background: #fff;
  color: #292a2e;
  padding: var(--common-tab-padding, 8px 12px);
  font-family: Outfit, sans-serif;
  font-size: 13px;
  font-weight: var(--common-tab-font-weight, 400);
  line-height: 20px;

  &:not(:last-child) {
    margin-inline-end: var(--common-tabs-gap, 8px);
  }

  &:hover:not([aria-disabled='true']) {
    background: #f8f8f8;
  }

  &:active:not([aria-disabled='true']) {
    background: #f0f1f2;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(17, 45, 115, 0.3);
  }

  &.is-active {
    border-color: #112d73;
    background: #112d73;
    color: #fff;
    font-weight: 500;

    &:hover:not([aria-disabled='true']) {
      background: linear-gradient(0deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.12) 100%), #112d73;
    }
  }

  &[aria-disabled='true'] {
    cursor: default;
  }
}

.common-tabs-mask {
  position: absolute;
  top: 0;
  right: 0;
  width: var(--common-tabs-mask-width);
  height: 100%;
  pointer-events: none;
  background: linear-gradient(270deg, #fff 25%, rgba(255, 255, 255, 0) 100%);
}

@media screen and (max-width: 1200px) {
  .common-tabs-wrap,
  .common-tabs {
    width: 100%;
    max-width: 100%;
  }
}
</style>
